import { useCallback, useEffect, useMemo, useState } from 'react';
import { Seletor, SeletorDeCliente } from '../Filtros';
import {
  BarraDeLista, BuscaDaBarra, FiltrosAtivos, ResumoQueFiltra, RodapeDaLista,
  usePaginacao, type FiltroAtivo, type ItemDoResumo,
} from '../BarraDeLista';
import { Periodo, type ValorDoPeriodo } from '../Periodo';
import {
  CamposDaFicha, TabelaComFicha, type ColunaComFicha, type FichaDaLinha,
} from '../TabelaComFicha';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, baixarBlob, ErroApi } from '../api';
import { data, numero, rotulo } from '../formato';
import { useSessao } from '../sessao';
import { useCaminho } from '../Caminho';
import { NomeDaAmostra } from '../NomeDaAmostra';
import type { Tom } from '../ui';
import {
  Area, Aviso, Campo, Carregando, Dialogo, ErroDaApi, Selo, Vazio,
} from '../ui';

/**
 * O boletim de medição — §13.4 a §13.7.
 *
 * ─── O que esta tela mostra que uma planilha não mostraria ───────────
 *
 * De onde vem cada número. A coluna «Nesta medição» abre a lista dos
 * ensaios que a compõem, com amostra, método, data de aprovação e o
 * nome de quem aprovou. É o §13.5, e é a diferença entre um boletim que
 * se defende e um que se digita.
 *
 * E o que ficou de FORA. `Sobra` é o executado que não coube: serviço
 * pronto, aprovado, que não entrou neste boletim porque passaria do
 * previsto. Um boletim que esconde isso parece fechado quando não está.
 *
 * ─── O que ela não faz ───────────────────────────────────────────────
 *
 * Não edita quantidade. A quantidade sai da apuração (§13.2); o ajuste
 * manual com justificativa é o passo seguinte e ainda não existe.
 * Enquanto isso, «Refazer a partir da apuração» é como se corrige — e
 * ele solta os ensaios do boletim antigo antes de remontar, para que
 * nada fique medido em dobro nem preso.
 */

const TOM: Record<string, Tom> = {
  rascunho: 'neutro',
  em_elaboracao: 'andamento',
  aguardando_conferencia: 'atencao',
  devolvida_para_correcao: 'critico',
  conferida: 'andamento',
  aguardando_aprovacao: 'atencao',
  aprovada: 'ok',
  emitida: 'ok',
  enviada_ao_cliente: 'ok',
  aceita_pelo_cliente: 'ok',
  contestada: 'critico',
  substituida: 'neutro',
  cancelada: 'neutro',
};

interface Medicao {
  id: string;
  numero: string;
  revisao: number;
  identificacao: string;
  status: string;
  periodo_inicio: string;
  periodo_fim: string;
  data_emissao: string | null;
  cliente: string;
  cliente_id?: string | null;
  obra: string;
  obra_codigo: string;
  obra_id: string;
  observacoes: string | null;
  motivo_devolucao: string | null;
  motivo_contestacao: string | null;
  enviada_em: string | null;
  aceita_em: string | null;
  contestada_em: string | null;
  elaborado_por_nome: string | null;
  conferido_por_nome: string | null;
  aprovado_por_nome: string | null;
  emitido_por_nome: string | null;
  itens: string;
  os_abrangidas: string;
  os_numeros: string | null;
  itens_com_sobra: string;
}

// ══════════════════════════════════════════════════════════════════════
// A lista
// ══════════════════════════════════════════════════════════════════════

export function Medicoes() {
  const [lista, setLista] = useState<Medicao[] | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const [busca, setBusca] = useState('');
  const [cliente, setCliente] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [recorte, setRecorte] = useState<string | null>(null);
  const [periodo, setPeriodo] = useState<ValorDoPeriodo>({ de: '', ate: '' });

  useEffect(() => {
    const abortar = new AbortController();
    api<Medicao[]>('/medicoes', { sinal: abortar.signal })
      .then((r) => { setLista(r); setErro(null); })
      .catch((e) => { if ((e as Error).name !== 'AbortError') setErro(e); });
    return () => abortar.abort();
  }, []);

  /*
   * A lista no padrão Carbon Testesolo (onda 272). Era uma tabela crua,
   * sem filtro e sem contagem — embora a API já filtrasse por obra,
   * cliente e situação. Os boletins são poucos (dezenas por ano), então o
   * recorte é feito aqui, na lista que já chegou, e os números do resumo
   * não encolhem quando um filtro está escolhido.
   *
   * O período cruza o PERÍODO MEDIDO do boletim (como a fila cruza o
   * intervalo do ensaio): o boletim de 21/09 a 20/10 aparece em outubro.
   */
  const RECORTES: (ItemDoResumo & { f(m: Medicao): boolean })[] = [
    { chave: 'elaboracao', rotulo: 'Em elaboração', aux: 'rascunho ou devolvida', tom: 'andamento', numero: 0,
      f: (m) => ['rascunho', 'em_elaboracao', 'devolvida_para_correcao'].includes(m.status) },
    { chave: 'conferencia', rotulo: 'Aguardando conferência', aux: 'esperando quem confere', tom: 'atencao', numero: 0,
      f: (m) => m.status === 'aguardando_conferencia' },
    { chave: 'aprovacao', rotulo: 'Aguardando aprovação', aux: 'conferida, falta aprovar', tom: 'atencao', numero: 0,
      f: (m) => ['conferida', 'aguardando_aprovacao'].includes(m.status) },
    { chave: 'contestadas', rotulo: 'Contestadas', aux: 'o cliente não aceitou', tom: 'critico', numero: 0,
      f: (m) => m.status === 'contestada' },
  ];
  const rec = RECORTES.find((r) => r.chave === recorte) ?? null;
  const resumo = RECORTES.map((r) => ({ ...r, numero: (lista ?? []).filter(r.f).length }));
  const filtradas = useMemo(() => {
    const b = busca.trim().toLowerCase();
    return (lista ?? []).filter((m) => {
      if (rec && !rec.f(m)) return false;
      if (status && m.status !== status) return false;
      if (cliente && m.cliente_id !== cliente) return false;
      if (periodo.de && m.periodo_fim < periodo.de) return false;
      if (periodo.ate && m.periodo_inicio > periodo.ate) return false;
      if (b && ![m.identificacao, m.obra, m.obra_codigo, m.cliente, m.os_numeros]
        .filter(Boolean).join(' ').toLowerCase().includes(b)) return false;
      return true;
    });
  }, [lista, rec, status, cliente, periodo.de, periodo.ate, busca]);
  const pag = usePaginacao(filtradas);
  const situacoes = useMemo(() => [...new Set((lista ?? []).map((m) => m.status))]
    .map((s) => ({ valor: s, texto: rotulo(s) })), [lista]);

  const ativos: FiltroAtivo[] = [];
  if (rec) ativos.push({ chave: 'rec', texto: rec.rotulo, tirar: () => setRecorte(null) });
  if (status) ativos.push({ chave: 'st', texto: `Situação: ${rotulo(status)}`, tirar: () => setStatus(null) });
  if (cliente) ativos.push({ chave: 'cli', texto: 'Cliente escolhido', tirar: () => setCliente(null) });
  if (busca) ativos.push({ chave: 'q', texto: `Busca: ${busca}`, tirar: () => setBusca('') });
  if (periodo.de || periodo.ate) {
    ativos.push({ chave: 'per', texto: `Período medido: ${periodo.de ? data(periodo.de) : '…'} – ${periodo.ate ? data(periodo.ate) : '…'}`, tirar: () => setPeriodo({ de: '', ate: '' }) });
  }

  return (
    <div>
      <div className="cabeca cabeca-lista">
        <div className="cresce">
          <h1>Medições</h1>
          <p className="sub">
            O boletim mede o que foi executado e aprovado, por período — o mais
            recente primeiro. Ele se abre a partir da ordem de serviço, onde a
            apuração mostra o que há para medir.
          </p>
        </div>
        {/*
          Os itens de medição são o catálogo de onde a quantidade sai —
          outra tela, com outra pergunta.
        */}
        <Link to="/itens-medicao" className="botao secundario">Itens de medição</Link>
      </div>

      <ResumoQueFiltra rotulo="Resumo das medições" itens={resumo} ativo={recorte}
        aoEscolher={setRecorte} />

      <BarraDeLista rotulo="Filtros das medições">
        <BuscaDaBarra valor={busca} aoMudar={setBusca}
          placeholder="Boletim, obra, cliente ou OS" rotulo="Buscar medição" />
        <Periodo valor={periodo} aoMudar={setPeriodo} rotulo="Período medido"
          atalhos={['mes', 'mesPassado', 'ultimos30', 'tudo']} />
        <SeletorDeCliente naBarra valor={cliente} aoMudar={setCliente} />
        <Seletor naBarra rotulo="Situação" valor={status} opcoes={situacoes}
          aoMudar={setStatus} todos="Todas as situações" />
      </BarraDeLista>

      <FiltrosAtivos itens={ativos}
        aoLimpar={() => {
          setRecorte(null); setStatus(null); setCliente(null); setBusca('');
          setPeriodo({ de: '', ate: '' });
        }}
        contagem={lista ? `${filtradas.length} de ${lista.length} ${lista.length === 1 ? 'boletim' : 'boletins'}` : undefined} />

      {erro ? <ErroDaApi erro={erro} /> : null}
      {lista === null ? <Carregando texto="Carregando as medições" /> : (
        <div className="cartao">
          {filtradas.length === 0 ? (
            <Vazio>
              {lista.length > 0
                ? 'Nenhum boletim com estes filtros. Use «Limpar filtros» para ver todos.'
                : 'Nenhuma medição ainda. Abra a ficha de uma OS: no bloco «Quantitativos executados» está o que já pode ser medido.'}
            </Vazio>
          ) : (
            <TabelaComFicha
              linhas={pag.visiveis}
              chave={(m) => m.id}
              nome={(m) => `Boletim ${m.identificacao}`}
              rotulo="Medições"
              colunas={COLUNAS_DA_MEDICAO}
              ficha={fichaDaMedicao}
            />
          )}
          <RodapeDaLista {...pag} ordem="período medido ↓" />
        </div>
      )}
    </div>
  );
}

const COLUNAS_DA_MEDICAO: ColunaComFicha<Medicao>[] = [
  {
    chave: 'boletim',
    cabeca: <th scope="col">Boletim</th>,
    celula: (m) => (
      <th scope="row">
        <Link to={`/medicao/${m.id}`} className="mono">{m.identificacao}</Link>
        <span className="sub">{data(m.periodo_inicio)} a {data(m.periodo_fim)}</span>
      </th>
    ),
  },
  {
    chave: 'obra',
    cabeca: <th scope="col">Obra e cliente</th>,
    celula: (m) => (
      <td>
        {m.obra} <span className="sub mono">{m.obra_codigo}</span>
        <span className="sub">{m.cliente}</span>
      </td>
    ),
  },
  {
    chave: 'itens', detalhe: true,
    cabeca: <th scope="col" className="num col-opcional">Itens</th>,
    celula: (m) => <td className="num mono col-opcional">{m.itens}</td>,
  },
  {
    chave: 'os', detalhe: true,
    cabeca: <th scope="col" className="col-opcional">OS</th>,
    celula: (m) => <td className="sub col-opcional">{m.os_numeros ?? '—'}</td>,
  },
  {
    chave: 'situacao',
    cabeca: <th scope="col">Situação</th>,
    celula: (m) => (
      <td>
        <Selo tom={TOM[m.status] ?? 'neutro'}>{rotulo(m.status)}</Selo>
        {Number(m.itens_com_sobra) > 0 && (
          <span className="sub aviso-linha">{m.itens_com_sobra} item(ns) acima do previsto</span>
        )}
      </td>
    ),
  },
];

function fichaDaMedicao(m: Medicao): FichaDaLinha {
  return {
    rotulo: 'Boletim de medição',
    titulo: m.identificacao,
    tituloCodigo: true,
    sub: <span>{m.obra} · {m.cliente}</span>,
    corpo: (
      <>
        <div className="tf-selos"><Selo tom={TOM[m.status] ?? 'neutro'}>{rotulo(m.status)}</Selo></div>
        {m.motivo_devolucao && <div className="tf-aviso critico">Devolvida: «{m.motivo_devolucao}»</div>}
        {m.motivo_contestacao && <div className="tf-aviso critico">Contestada: «{m.motivo_contestacao}»</div>}
        <CamposDaFicha itens={[
          ['Período', `${data(m.periodo_inicio)} a ${data(m.periodo_fim)}`],
          ['OS', m.os_numeros],
          ['Itens', m.itens],
          ['Elaborou', m.elaborado_por_nome],
          ['Conferiu', m.conferido_por_nome],
          ['Aprovou', m.aprovado_por_nome],
          ['Emitida em', m.data_emissao ? data(m.data_emissao) : null],
          ['Enviada ao cliente', m.enviada_em ? data(m.enviada_em) : null],
          ['Aceita em', m.aceita_em ? data(m.aceita_em) : null],
        ]} />
      </>
    ),
    acoes: <Link className="botao" to={`/medicao/${m.id}`}>Abrir boletim</Link>,
  };
}

// ══════════════════════════════════════════════════════════════════════
// A ficha
// ══════════════════════════════════════════════════════════════════════

interface Origem {
  ensaio_id: string; metodo: string; amostra: string | null;
  identificacao: string | null; data_aprovacao: string | null;
  /** 303: a OS da amostra — o boletim pode juntar várias. */
  os_numero?: string | null;
  aprovado_por: string | null; quantidade: string;
}

interface Item {
  id: string;
  item_codigo: string;
  item_descricao: string;
  item_categoria: string | null;
  unidade_nome: string;
  qtd_prevista: string | null;
  qtd_executada_acumulada: string | null;
  qtd_medida_anterior: string | null;
  qtd_medicao_atual: string;
  qtd_medida_acumulada: string | null;
  saldo_remanescente: string | null;
  qtd_executada_nao_medida: string | null;
  qtd_executada_nao_aprovada: string | null;
  percentual_medido: string | null;
  qtd_apurada_automatica: string | null;
  qtd_ajustada: string | null;
  justificativa_ajuste: string | null;
  justificativa_excedente: string | null;
  autorizacao_excedente: string | null;
  origem: Origem[];
}

interface Ficha extends Medicao {
  itens_lista?: Item[];
}

/**
 * Os passos do §13.4, do jeito que a pessoa na tela pensa neles.
 *
 * O rótulo é o VERBO, e não o estado de destino: quem aperta está
 * fazendo alguma coisa, não escolhendo um substantivo num menu. E cada
 * botão declara a permissão que ele exige — quem não tem, não vê o
 * botão em vez de ver um erro depois de clicar.
 */
const PASSOS: Array<{
  de: string; para: string; verbo: string; perm: string; motivo?: boolean;
  tom?: 'primario' | 'perigo';
}> = [
  { de: 'rascunho', para: 'em_elaboracao', verbo: 'Começar a elaborar',
    perm: 'medicao.elaborar', tom: 'primario' },
  { de: 'em_elaboracao', para: 'aguardando_conferencia',
    verbo: 'Mandar conferir', perm: 'medicao.elaborar', tom: 'primario' },
  { de: 'devolvida_para_correcao', para: 'aguardando_conferencia',
    verbo: 'Mandar conferir de novo', perm: 'medicao.elaborar', tom: 'primario' },
  { de: 'aguardando_conferencia', para: 'conferida', verbo: 'Conferir',
    perm: 'medicao.conferir', tom: 'primario' },
  { de: 'aguardando_conferencia', para: 'devolvida_para_correcao',
    verbo: 'Devolver para correção', perm: 'medicao.conferir', motivo: true },
  { de: 'conferida', para: 'aguardando_aprovacao', verbo: 'Enviar para aprovação',
    perm: 'medicao.elaborar', tom: 'primario' },
  { de: 'aguardando_aprovacao', para: 'aprovada', verbo: 'Aprovar',
    perm: 'medicao.aprovar', tom: 'primario' },
  { de: 'aguardando_aprovacao', para: 'devolvida_para_correcao',
    verbo: 'Devolver para correção', perm: 'medicao.conferir', motivo: true },
  /*
   * Depois da emissão, o boletim sai do sistema — e o que se registra
   * aqui é o que aconteceu FORA dele: o e-mail que foi mandado, o
   * aceite que voltou, a contestação que chegou. Enquanto não houver
   * portal do cliente, é isto que o §13.4 pode ser sem mentir.
   */
  { de: 'emitida', para: 'enviada_ao_cliente',
    verbo: 'Registrar envio ao cliente', perm: 'medicao.elaborar' },
  { de: 'enviada_ao_cliente', para: 'aceita_pelo_cliente',
    verbo: 'Registrar aceite do cliente', perm: 'medicao.elaborar',
    tom: 'primario' },
  { de: 'enviada_ao_cliente', para: 'contestada',
    verbo: 'Registrar contestação', perm: 'medicao.elaborar', motivo: true },
];

export function MedicaoFicha() {
  const { id = '' } = useParams();
  const { pode } = useSessao();
  const navegar = useNavigate();
  const [d, setD] = useState<Ficha | null>(null);
  useCaminho(d ? [{ rotulo: '', codigo: d.identificacao }] : null);
  const [itens, setItens] = useState<Item[]>([]);
  const [erro, setErro] = useState<unknown>(null);
  const [aberto, setAberto] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState<string | null>(null);
  const [pedindoMotivo, setPedindoMotivo] = useState<string | null>(null);
  const [motivo, setMotivo] = useState('');
  const [irmas, setIrmas] = useState<Array<{ id: string; numero: string }>>([]);
  const [ajustando, setAjustando] = useState<Item | null>(null);
  const [incluindo, setIncluindo] = useState<Item | null>(null);
  const [revisando, setRevisando] = useState(false);

  const carregar = useCallback(async () => {
    try {
      const r = await api<Ficha & { itens: Item[] }>(`/medicoes/${id}`);
      setD(r);
      setItens(r.itens ?? []);
      setErro(null);
    } catch (e) { setErro(e); }
  }, [id]);

  useEffect(() => { void carregar(); }, [carregar]);

  /*
   * As outras OS da MESMA obra.
   *
   * «Uma medição agrupando várias OS da mesma obra ou contrato»
   * (§13.7). O caso real é o lote que chega depois: a obra 2608194 tem a
   * OS original e a 2608194-A, e o cliente espera um boletim só.
   *
   * A lista é da obra do boletim e não do sistema inteiro — a API recusa
   * OS de outra obra, e oferecer o que vai ser recusado é ensinar a
   * tentar.
   */
  useEffect(() => {
    if (!d?.obra_id) return;
    const abortar = new AbortController();
    api<Array<{ id: string; numero: string }>>(
      `/os?obra=${d.obra_id}&limite=200&arquivadas=incluir`, { sinal: abortar.signal },
    ).then(setIrmas)
      .catch((e) => { if ((e as Error).name !== 'AbortError') setIrmas([]); });
    return () => abortar.abort();
  }, [d?.obra_id]);

  async function mexerNaOs(osId: string, incluir: boolean) {
    setOcupado(`os:${osId}`); setErro(null);
    try {
      await api(`/medicoes/${id}/os/${osId}`,
        incluir ? { metodo: 'POST', corpo: {} } : { metodo: 'DELETE' });
      await carregar();
    } catch (e) { setErro(e); }
    finally { setOcupado(null); }
  }

  async function mover(para: string, comMotivo?: string) {
    setOcupado(para); setErro(null);
    try {
      await api(`/medicoes/${id}/status`, {
        metodo: 'POST', corpo: { para, motivo: comMotivo || undefined },
      });
      setPedindoMotivo(null); setMotivo('');
      await carregar();
    } catch (e) { setErro(e); }
    finally { setOcupado(null); }
  }

  /**
   * Baixar o documento ou a planilha.
   *
   * Um caminho só para os três formatos (§13.6). Eles saem do mesmo
   * quadro no servidor — o que muda é a embalagem, e o `download`
   * carrega o nome do boletim para o arquivo não chegar na pasta do
   * cliente chamado «documento (3)».
   */
  async function baixar(caminho: string, ext: string) {
    setOcupado(ext); setErro(null);
    try {
      const blob = await baixarBlob(`/medicoes/${id}/${caminho}`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `BM-${d?.numero ?? id}-R${
        String(d?.revisao ?? 0).padStart(2, '0')}.${ext}`;
      document.body.appendChild(a); a.click(); a.remove();
      /* Sem o revoke, cada download deixa um blob preso na memória da
         aba — e esta tela fica aberta o mês inteiro. */
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch (e) { setErro(e); }
    finally { setOcupado(null); }
  }

  async function emitir() {
    setOcupado('emitir'); setErro(null);
    try {
      await api(`/medicoes/${id}/emitir`, { metodo: 'POST', corpo: {} });
      await carregar();
    } catch (e) { setErro(e); }
    finally { setOcupado(null); }
  }

  async function revisar(motivo: string) {
    setOcupado('revisar'); setErro(null);
    try {
      const nova = await api<{ id: string }>(`/medicoes/${id}/revisar`, {
        metodo: 'POST', corpo: { motivo },
      });
      setRevisando(false);
      navegar(`/medicao/${nova.id}`);
    } catch (e) { setErro(e); }
    finally { setOcupado(null); }
  }

  async function remontar() {
    setOcupado('montar'); setErro(null);
    try {
      await api(`/medicoes/${id}/montar`, { metodo: 'POST', corpo: {} });
      await carregar();
    } catch (e) { setErro(e); }
    finally { setOcupado(null); }
  }

  if (erro && !d) return <ErroDaApi erro={erro} />;
  if (!d) return <Carregando texto="Abrindo o boletim" />;

  const passos = PASSOS.filter((p) => p.de === d.status && pode(p.perm));
  const aberta = !['aprovada', 'emitida', 'enviada_ao_cliente',
    'aceita_pelo_cliente', 'contestada', 'substituida', 'cancelada']
    .includes(d.status);
  const sobra = Number(d.itens_com_sobra);

  return (
    <div className="tela medicao">
      <div className="cabeca">
        <div className="linha-topo">
          <h1 className="cresce mono">{d.identificacao}</h1>
        </div>
        <p className="sub">{d.obra} · {d.cliente}</p>
        <p className="meta">
          Período de {data(d.periodo_inicio)} a {data(d.periodo_fim)}
          {d.os_numeros && ` · OS ${d.os_numeros}`}
        </p>
      </div>

      {erro ? <ErroDaApi erro={erro} /> : null}

      {d.status === 'devolvida_para_correcao' && d.motivo_devolucao && (
        <Aviso tipo="atencao" titulo="Devolvida para correção">
          {d.motivo_devolucao}
        </Aviso>
      )}

      {d.status === 'contestada' && (
        <Aviso tipo="erro" titulo="Contestado pelo cliente">
          {d.motivo_contestacao ?? 'Sem motivo registrado.'}
          <br />
          Contestação não se resolve sozinha: ou o boletim é substituído
          por uma revisão, ou a divergência é esclarecida e registrada.
        </Aviso>
      )}

      {/* ── Situação e os passos ─────────────────────────────────── */}

      <div className="cartao">
        <div className="cartao-cabeca">
          <h2>Situação</h2>
          <Selo tom={TOM[d.status] ?? 'neutro'}>{rotulo(d.status)}</Selo>
        </div>

        <dl className="assinaturas">
          <div><dt>Elaborado por</dt>
            <dd>{d.elaborado_por_nome ?? <span className="nota">—</span>}</dd></div>
          <div><dt>Conferido por</dt>
            <dd>{d.conferido_por_nome ?? <span className="nota">—</span>}</dd></div>
          <div><dt>Aprovado por</dt>
            <dd>{d.aprovado_por_nome ?? <span className="nota">—</span>}</dd></div>
          <div><dt>Emitido por</dt>
            <dd>{d.emitido_por_nome ?? <span className="nota">—</span>}</dd></div>
          {/*
            * O cliente aparece na mesma fileira das assinaturas porque é
            * a mesma pergunta: quantos passos faltam para este boletim
            * estar fechado. Só aparece depois que houve envio — antes
            * dele, «— » ali seria uma cobrança sem sentido.
            */}
          {d.enviada_em && (
            <div><dt>Enviado ao cliente</dt>
              <dd>{data(d.enviada_em)}</dd></div>
          )}
          {d.aceita_em && (
            <div><dt>Aceito pelo cliente</dt>
              <dd>{data(d.aceita_em)}</dd></div>
          )}
        </dl>

        <div className="linha">
          {passos.map((p) => (
            <button key={p.para + p.verbo} type="button"
              className={`botao${p.tom === 'primario' ? '' : ' secundario'}`}
              disabled={!!ocupado}
              onClick={() => (p.motivo
                ? setPedindoMotivo(p.para)
                : void mover(p.para))}>
              {ocupado === p.para ? 'Aguarde…' : p.verbo}
            </button>
          ))}
          {aberta && pode('medicao.elaborar') && (
            <button type="button" className="botao secundario"
              disabled={!!ocupado} onClick={() => void remontar()}>
              {ocupado === 'montar'
                ? 'Refazendo…' : 'Refazer a partir da apuração'}
            </button>
          )}
          {d.status === 'aprovada' && pode('medicao.aprovar') && (
            <button type="button" className="botao"
              disabled={!!ocupado} onClick={() => void emitir()}>
              {ocupado === 'emitir' ? 'Emitindo…' : 'Emitir boletim'}
            </button>
          )}
          {/*
            * Revisar é o caminho para corrigir o que já está fechado
            * (§13.6). Ele NÃO aparece enquanto o boletim está aberto —
            * ali o certo é «Refazer a partir da apuração», que não gasta
            * número nem deixa dois documentos na história.
            */}
          {!aberta && d.status !== 'substituida' && d.status !== 'cancelada'
            && pode('medicao.elaborar') && (
            <button type="button" className="botao secundario"
              disabled={!!ocupado} onClick={() => setRevisando(true)}>
              Abrir revisão
            </button>
          )}
        </div>

        {/*
          * Os três formatos do §13.6, sempre disponíveis.
          *
          * Antes de aprovado, o PDF sai com marca d'água de preliminar —
          * quem confere precisa poder imprimir para conferir, e um
          * rascunho que sai parecendo definitivo é o caminho mais curto
          * para alguém faturar contra números que ninguém assinou.
          */}
        <div className="linha">
          <button type="button" className="botao discreto" disabled={!!ocupado}
            onClick={() => void baixar('documento.pdf', 'pdf')}>
            {ocupado === 'pdf' ? 'Gerando…' : 'PDF'}
          </button>
          <button type="button" className="botao discreto" disabled={!!ocupado}
            onClick={() => void baixar('planilha.xlsx', 'xlsx')}>
            {ocupado === 'xlsx' ? 'Gerando…' : 'Excel'}
          </button>
          <button type="button" className="botao discreto" disabled={!!ocupado}
            onClick={() => void baixar('planilha.csv', 'csv')}>
            {ocupado === 'csv' ? 'Gerando…' : 'CSV'}
          </button>
          {!['aprovada', 'emitida', 'enviada_ao_cliente',
            'aceita_pelo_cliente'].includes(d.status) && (
            <span className="dica">
              O PDF sai marcado como preliminar enquanto não for aprovado.
            </span>
          )}
        </div>

        {!aberta && (
          <p className="dica">
            Esta medição está fechada. Os números dela não se alteram mais
            (§13.6) — corrigir exige uma revisão ou uma medição substituta.
          </p>
        )}

        {['emitida', 'enviada_ao_cliente'].includes(d.status) && (
          <p className="rodape-regra">
            O portal onde o cliente entra e responde ainda não existe. Até
            ele existir, o boletim vai por fora — e o que se registra aqui
            é o que aconteceu lá: a data do envio, o aceite que voltou, a
            contestação que chegou. É menos do que o §13.4 pede, e é
            honesto: um estado que a tela não alcança é um estado que não
            existe.
          </p>
        )}
      </div>

      {/* ── As OS abrangidas (§13.7) ─────────────────────────────── */}

      {aberta && pode('medicao.elaborar') && irmas.length > 1 && (
        <div className="cartao">
          <div className="cartao-cabeca">
            <h2>Ordens de serviço deste boletim</h2>
            <span className="sub">
              {d.os_abrangidas} de {irmas.length} da obra
            </span>
          </div>
          <p className="sub">
            Esta obra tem mais de uma OS. Um boletim pode agrupar as da
            mesma obra (§13.7) — é o caso do lote que chega depois, e o
            cliente espera um documento só. Incluir ou retirar refaz a
            montagem na hora.
          </p>
          <ul className="lista-codigos">
            {irmas.map((o) => {
              const dentro = (d.os_numeros ?? '').split(', ').includes(o.numero);
              return (
                <li key={o.id} className={dentro ? 'marcado' : undefined}>
                  <span className="mono cresce">{o.numero}</span>
                  <button type="button" className="botao secundario"
                    disabled={!!ocupado}
                    onClick={() => void mexerNaOs(o.id, !dentro)}>
                    {ocupado === `os:${o.id}` ? 'Aguarde…'
                      : dentro ? 'Retirar' : 'Incluir'}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* ── O que ficou de fora ──────────────────────────────────── */}

      {sobra > 0 && (
        <Aviso tipo="atencao" titulo="Há serviço executado que não entrou">
          {sobra} item(ns) têm quantidade executada e aprovada que não coube
          neste boletim, por passar do previsto no escopo contratado. O
          §13.3 pede que a quantidade excedente seja destacada e vinculada
          a justificativa, aditivo ou autorização — ela não entra sozinha.
        </Aviso>
      )}

      {/* ── Os itens ─────────────────────────────────────────────── */}

      <div className="cartao quantitativos">
        <div className="cartao-cabeca">
          <h2>Itens medidos</h2>
          <span className="sub">{itens.length} item(ns)</span>
        </div>

        {itens.length === 0 ? (
          <Vazio>
            Nenhum item. Ou não há ensaio aprovado dentro do período, ou
            tudo o que havia já foi medido em outro boletim.
          </Vazio>
        ) : (
          <div className="rola">
            <table className="dados">
              <thead>
                <tr>
                  <th>Código</th><th>Item</th><th>Unidade</th>
                  <th className="num">Previsto</th>
                  <th className="num">Medido antes</th>
                  <th className="num">Nesta medição</th>
                  <th className="num">Acumulado</th>
                  <th className="num">Saldo</th>
                  <th className="num">Sobra</th>
                </tr>
              </thead>
              <tbody>
                {itens.map((i) => {
                  const eu = aberto === i.id;
                  const naoMedida = Number(i.qtd_executada_nao_medida ?? 0);
                  return [
                    <tr key={i.id} className={`abrivel${eu ? ' aberta' : ''}`}
                      onClick={() => setAberto(eu ? null : i.id)}>
                      <td className="mono">{i.item_codigo}</td>
                      <td>{i.item_descricao}</td>
                      <td>{i.unidade_nome}</td>
                      <td className="num mono">
                        {i.qtd_prevista == null
                          ? <span className="nota">não previsto</span>
                          : numero(i.qtd_prevista, 2, true)}
                      </td>
                      <td className="num mono">
                        {numero(i.qtd_medida_anterior ?? 0, 2, true)}
                      </td>
                      <td className="num mono forte">
                        {numero(i.qtd_medicao_atual, 2, true)}
                      </td>
                      <td className="num mono">
                        {numero(i.qtd_medida_acumulada ?? 0, 2, true)}
                      </td>
                      <td className="num mono">
                        {i.saldo_remanescente == null
                          ? <span className="nota">—</span>
                          : numero(i.saldo_remanescente, 2, true)}
                      </td>
                      <td className="num mono">
                        {naoMedida > 0
                          ? <Selo tom="atencao">
                              {numero(i.qtd_executada_nao_medida, 2, true)}
                            </Selo>
                          : <span className="nota">—</span>}
                      </td>
                    </tr>,
                    eu ? (
                      <tr key={`${i.id}-ev`} className="detalhe">
                        <td colSpan={9}>
                          <h3>O que está sendo medido aqui</h3>
                          {i.origem.length === 0 ? (
                            <p className="nota">Sem origem registrada.</p>
                          ) : (
                            <table className="dados compacta">
                              <thead>
                                <tr>
                                  <th>Amostra</th><th>Método</th>
                                  <th className="num">Vale</th>
                                  <th>Aprovado em</th><th>Por</th>
                                </tr>
                              </thead>
                              <tbody>
                                {i.origem.map((o) => (
                                  <tr key={o.ensaio_id}>
                                    <td>
                                      {/* 303 (RN-248): o nome do cliente em
                                          destaque, o código e a OS embaixo. */}
                                      <Link to={`/ensaio/${o.ensaio_id}`}>
                                        {o.amostra || o.identificacao
                                          ? <NomeDaAmostra amostra={{
                                            codigo: o.amostra, identificacao: o.identificacao,
                                            os: o.os_numero }} />
                                          : '—'}
                                      </Link>
                                    </td>
                                    <td className="mono">{o.metodo}</td>
                                    <td className="num mono">
                                      {numero(o.quantidade, 2, true)}
                                    </td>
                                    <td>{o.data_aprovacao
                                      ? data(o.data_aprovacao)
                                      : <span className="nota">—</span>}</td>
                                    <td>{o.aprovado_por
                                      ?? <span className="nota">—</span>}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                          {naoMedida > 0 && (
                            <p className="dica">
                              {numero(i.qtd_executada_nao_medida, 2, true)}{' '}
                              {i.unidade_nome.toLowerCase()} executados e
                              aprovados ficaram de fora: entrariam acima do
                              previsto.
                            </p>
                          )}
                          {i.qtd_apurada_automatica != null
                            && i.qtd_ajustada != null && (
                            <p className="dica">
                              <b>Ajustado à mão.</b> O sistema apurou{' '}
                              {numero(i.qtd_apurada_automatica, 2, true)}; vale{' '}
                              {numero(i.qtd_medicao_atual, 2, true)}.{' '}
                              {i.justificativa_ajuste}
                            </p>
                          )}
                          {i.justificativa_excedente
                            || i.autorizacao_excedente ? (
                              <p className="dica">
                                <b>Excedente autorizado.</b>{' '}
                                {i.autorizacao_excedente
                                  && <span className="mono">
                                    {i.autorizacao_excedente}{' '}
                                  </span>}
                                {i.justificativa_excedente}
                              </p>
                            ) : null}
                          {aberta && pode('medicao.ajustar') && (
                            <div className="linha" onClick={(ev) => ev.stopPropagation()}>
                              <button type="button" className="botao discreto"
                                disabled={!!ocupado}
                                onClick={() => setAjustando(i)}>
                                Ajustar quantidade
                              </button>
                              {naoMedida > 0 && (
                                <button type="button" className="botao discreto"
                                  disabled={!!ocupado}
                                  onClick={() => setIncluindo(i)}>
                                  Incluir o excedente
                                </button>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    ) : null,
                  ];
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {ajustando && (
        <DialogoAjuste item={ajustando} medicaoId={id}
          aoFechar={() => setAjustando(null)}
          aoPronto={() => { setAjustando(null); void carregar(); }} />
      )}

      {incluindo && (
        <DialogoExcedente item={incluindo} medicaoId={id}
          aoFechar={() => setIncluindo(null)}
          aoPronto={() => { setIncluindo(null); void carregar(); }} />
      )}

      {revisando && (
        <DialogoRevisao aoFechar={() => setRevisando(false)}
          ocupado={ocupado === 'revisar'}
          aoConfirmar={(motivo) => void revisar(motivo)} />
      )}

      {pedindoMotivo && (
        <Dialogo aberto
          titulo={pedindoMotivo === 'contestada'
            ? 'Registrar contestação do cliente' : 'Devolver para correção'}
          aoFechar={() => setPedindoMotivo(null)}>
          <p className="sub">
            {pedindoMotivo === 'contestada'
              ? 'Escreva o que o cliente contestou. Sem isso, quem for '
                + 'corrigir não sabe por onde começar.'
              : 'Quem recebe de volta precisa saber o que corrigir. O '
                + 'motivo fica no boletim e na trilha de auditoria.'}
          </p>
          <Area rotulo="Motivo" rows={3} value={motivo}
            onChange={(e) => setMotivo(e.target.value)} />
          <div className="rodape-dialogo">
            <button type="button" className="botao"
              disabled={motivo.trim().length < 5 || !!ocupado}
              onClick={() => void mover(pedindoMotivo, motivo.trim())}>
              {pedindoMotivo === 'contestada' ? 'Registrar' : 'Devolver'}
            </button>
            <button type="button" className="botao secundario"
              onClick={() => setPedindoMotivo(null)}>Cancelar</button>
          </div>
        </Dialogo>
      )}
    </div>
  );
}

/**
 * Ajustar a quantidade apurada de um item — §13.2.
 *
 * É o único lugar do módulo onde um número deixa de ser consequência do
 * que aconteceu e passa a ser afirmação de alguém. Por isso o diálogo
 * mostra, lado a lado, o que o sistema apurou e o que está prestes a
 * valer — e não deixa gravar sem o motivo escrito.
 */
function DialogoAjuste(
  { item, medicaoId, aoFechar, aoPronto }:
  { item: Item; medicaoId: string; aoFechar(): void; aoPronto(): void },
) {
  const automatico = item.qtd_apurada_automatica ?? item.qtd_medicao_atual;
  const [quantidade, setQuantidade] = useState(
    String(item.qtd_medicao_atual).replace('.', ','));
  const [justificativa, setJustificativa] = useState('');
  const [erro, setErro] = useState<unknown>(null);
  const [enviando, setEnviando] = useState(false);

  const valor = Number(quantidade.replace(/\./g, '').replace(',', '.'));
  const pronto = Number.isFinite(valor) && valor >= 0
    && justificativa.trim().length >= 5;

  async function enviar() {
    setErro(null); setEnviando(true);
    try {
      await api(`/medicoes/${medicaoId}/itens/${item.id}/ajustar`, {
        metodo: 'POST',
        corpo: { quantidade: valor, justificativa: justificativa.trim() },
      });
      aoPronto();
    } catch (e) { setErro(e); }
    finally { setEnviando(false); }
  }

  return (
    <Dialogo aberto titulo="Ajustar quantidade medida" aoFechar={aoFechar}>
      <ErroDaApi erro={erro} />
      <p className="sub">
        <span className="mono">{item.item_codigo}</span> —{' '}
        {item.item_descricao}
      </p>
      <p className="dica">
        O sistema apurou <b>{numero(automatico, 2, true)}</b>{' '}
        {item.unidade_nome.toLowerCase()} a partir dos ensaios aprovados. O
        valor automático continua guardado ao lado do ajustado; o ajuste
        não mexe nos ensaios ligados a este item.
      </p>
      <Campo rotulo="Quantidade que vale" inputMode="decimal"
        value={quantidade} onChange={(e) => setQuantidade(e.target.value)}
        autoFocus />
      <Area rotulo="Justificativa" rows={3} value={justificativa}
        onChange={(e) => setJustificativa(e.target.value)}
        dica="Fica no boletim e na trilha de auditoria." />
      <div className="rodape-dialogo">
        <button type="button" className="botao" disabled={!pronto || enviando}
          onClick={() => void enviar()}>
          {enviando ? 'Gravando…' : 'Ajustar'}
        </button>
        <button type="button" className="botao secundario" onClick={aoFechar}
          disabled={enviando}>Cancelar</button>
      </div>
    </Dialogo>
  );
}

/**
 * Trazer o excedente para dentro — §13.3.
 *
 * A montagem para no previsto de propósito. Este diálogo é o ato que
 * decide o contrário, e ele não existe sem o texto da decisão: o §13.3
 * pede que a quantidade excedente seja «vinculada a uma justificativa,
 * aditivo ou autorização».
 */
function DialogoExcedente(
  { item, medicaoId, aoFechar, aoPronto }:
  { item: Item; medicaoId: string; aoFechar(): void; aoPronto(): void },
) {
  const [justificativa, setJustificativa] = useState('');
  const [autorizacao, setAutorizacao] = useState('');
  const [erro, setErro] = useState<unknown>(null);
  const [enviando, setEnviando] = useState(false);

  const pronto = justificativa.trim().length >= 5 || autorizacao.trim().length > 0;

  async function enviar() {
    setErro(null); setEnviando(true);
    try {
      await api(`/medicoes/${medicaoId}/itens/${item.id}/excedente`, {
        metodo: 'POST',
        corpo: {
          justificativa: justificativa.trim() || undefined,
          autorizacao: autorizacao.trim() || undefined,
        },
      });
      aoPronto();
    } catch (e) { setErro(e); }
    finally { setEnviando(false); }
  }

  return (
    <Dialogo aberto titulo="Incluir o excedente" aoFechar={aoFechar}>
      <ErroDaApi erro={erro} />
      <p className="sub">
        <span className="mono">{item.item_codigo}</span> —{' '}
        {item.item_descricao}
      </p>
      <Aviso tipo="atencao" titulo="Isto passa do contratado">
        {numero(item.qtd_executada_nao_medida, 2, true)}{' '}
        {item.unidade_nome.toLowerCase()} foram executados e aprovados
        além do previsto no escopo. Incluí-los aqui é uma decisão
        comercial: ela fica registrada com o seu nome, e o boletim passa a
        cobrar acima do contratado.
      </Aviso>
      <Campo rotulo="Aditivo ou autorização" value={autorizacao}
        onChange={(e) => setAutorizacao(e.target.value)}
        dica="Número do aditivo, do e-mail ou da autorização do cliente." />
      <Area rotulo="Justificativa" rows={3} value={justificativa}
        onChange={(e) => setJustificativa(e.target.value)}
        dica="Um dos dois basta; os dois é melhor." />
      <div className="rodape-dialogo">
        <button type="button" className="botao" disabled={!pronto || enviando}
          onClick={() => void enviar()}>
          {enviando ? 'Incluindo…' : 'Incluir no boletim'}
        </button>
        <button type="button" className="botao secundario" onClick={aoFechar}
          disabled={enviando}>Cancelar</button>
      </div>
    </Dialogo>
  );
}

/**
 * Abrir a revisão — §13.6.
 *
 * O diálogo diz o que vai acontecer antes de acontecer: o boletim atual
 * é substituído, os ensaios dele voltam a ficar disponíveis, e a revisão
 * nasce montada com o mesmo número e a revisão seguinte.
 */
function DialogoRevisao(
  { aoFechar, aoConfirmar, ocupado }:
  { aoFechar(): void; aoConfirmar(motivo: string): void; ocupado: boolean },
) {
  const [motivo, setMotivo] = useState('');
  return (
    <Dialogo aberto titulo="Abrir revisão do boletim" aoFechar={aoFechar}>
      <p className="sub">
        O boletim atual passa a <b>substituído</b> e continua na história —
        nada é apagado. Os ensaios que ele media voltam a ficar
        disponíveis, e a revisão nasce montada, com o mesmo número e a
        revisão seguinte.
      </p>
      <Area rotulo="Motivo da revisão" rows={3} value={motivo}
        onChange={(e) => setMotivo(e.target.value)}
        dica="Vai impresso no boletim novo e na trilha de auditoria." />
      <div className="rodape-dialogo">
        <button type="button" className="botao"
          disabled={motivo.trim().length < 5 || ocupado}
          onClick={() => aoConfirmar(motivo.trim())}>
          {ocupado ? 'Abrindo…' : 'Abrir revisão'}
        </button>
        <button type="button" className="botao secundario" onClick={aoFechar}
          disabled={ocupado}>Cancelar</button>
      </div>
    </Dialogo>
  );
}

/**
 * Abrir um boletim a partir da ficha da OS.
 *
 * Fica aqui, e não em `OsForms`, porque o assunto é medição: quem for
 * mexer no que o boletim faz vai procurar neste arquivo.
 */
export function FormNovaMedicao(
  { osId, obraId, aoFechar }:
  { osId: string; obraId: string; aoFechar(): void },
) {
  const navegar = useNavigate();
  const hoje = new Date();
  const primeiro = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
  const iso = (x: Date) => [
    x.getFullYear(),
    String(x.getMonth() + 1).padStart(2, '0'),
    String(x.getDate()).padStart(2, '0'),
  ].join('-');

  const [inicio, setInicio] = useState(iso(primeiro));
  const [fim, setFim] = useState(iso(hoje));
  const [observacoes, setObservacoes] = useState('');
  const [erro, setErro] = useState<unknown>(null);
  const [enviando, setEnviando] = useState(false);
  const erroApi = erro instanceof ErroApi ? erro : null;

  async function enviar() {
    setErro(null); setEnviando(true);
    try {
      const m = await api<{ id: string }>('/medicoes', {
        metodo: 'POST',
        corpo: {
          obraId,
          periodoInicio: inicio,
          periodoFim: fim,
          osIds: [osId],
          observacoes: observacoes.trim() || undefined,
        },
      });
      navegar(`/medicao/${m.id}`);
    } catch (e) { setErro(e); }
    finally { setEnviando(false); }
  }

  return (
    <Dialogo aberto titulo="Abrir boletim de medição" aoFechar={aoFechar}>
      <ErroDaApi erro={erro} />
      <p className="sub">
        Entra no boletim o que foi executado e <b>aprovado</b> dentro do
        período, desta OS, e que ainda não foi medido em outro boletim. O
        número sai sozinho, do código da obra.
      </p>
      <div className="form linha">
        <Campo rotulo="Início do período" type="date" value={inicio}
          onChange={(e) => setInicio(e.target.value)}
          erro={erroApi?.campo('periodoInicio')} />
        <Campo rotulo="Fim do período" type="date" value={fim}
          onChange={(e) => setFim(e.target.value)}
          erro={erroApi?.campo('periodoFim')} />
      </div>
      <Area rotulo="Observações" rows={2} value={observacoes}
        onChange={(e) => setObservacoes(e.target.value)} />
      <div className="rodape-dialogo">
        <button type="button" className="botao" disabled={enviando}
          onClick={() => void enviar()}>
          {enviando ? 'Abrindo…' : 'Abrir boletim'}
        </button>
        <button type="button" className="botao secundario" onClick={aoFechar}
          disabled={enviando}>Cancelar</button>
      </div>
    </Dialogo>
  );
}
